import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { PaymentTypes } from "../../../common/enums";
import {
  IOrdersPageViewModel,
  IOrderViewModel,
} from "../models/cashdeskModels";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";

export const useCashdeskOrders = () => {
  const { selectedStudioId } = useAppContext();
  const { mutate } = useApiMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Filters ───────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<PaymentTypes | null>(null);
  const [page, setPage] = useState(1);

  // ─── Detail modal ──────────────────────────────────────────────
  const [selectedOrder, setSelectedOrder] = useState<IOrderViewModel | null>(
    null,
  );

  // ─── URL param: orderId (deep-link to a specific order) ────────
  // Captured once so the param can be stripped from the URL without losing
  // the target. We fetch the order by id (below) rather than searching the
  // loaded page, so it opens even for older orders that aren't on page 1 —
  // e.g. when arriving from a row in a user's history.
  const [deepLinkOrderId] = useState<number | null>(() => {
    const raw = searchParams.get("orderId");
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (deepLinkOrderId != null && searchParams.has("orderId")) {
      searchParams.delete("orderId");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: deepLinkOrder } = useApiQuery<IOrderViewModel>({
    queryKey: [
      endpointsMetadata.cashdesk.getOrderById,
      selectedStudioId,
      deepLinkOrderId,
    ],
    endpoint: `${endpointsMetadata.cashdesk.getOrderById}?id=${deepLinkOrderId}`,
    enabled: selectedStudioId > 0 && deepLinkOrderId != null,
    globalIsLoading: false,
  });

  useEffect(() => {
    if (deepLinkOrder) {
      setSelectedOrder(deepLinkOrder);
    }
  }, [deepLinkOrder]);

  // ─── Build query string ────────────────────────────────────────
  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", "30");
    if (search) params.set("search", search);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (paymentFilter) params.set("paymentTypeId", String(paymentFilter));
    return params.toString();
  }, [page, search, dateFrom, dateTo, paymentFilter]);

  const { data, isLoading } = useApiQuery<IOrdersPageViewModel>({
    queryKey: [
      endpointsMetadata.cashdesk.getOrders,
      selectedStudioId,
      page,
      search,
      dateFrom,
      dateTo,
      paymentFilter,
    ],
    endpoint: `${endpointsMetadata.cashdesk.getOrders}?${buildQueryString()}`,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  // ─── Accumulated orders for infinite scroll ────────────────────
  const [allOrders, setAllOrders] = useState<IOrderViewModel[]>([]);

  useEffect(() => {
    if (data?.items) {
      if (page === 1) {
        setAllOrders(data.items);
      } else {
        setAllOrders((prev) => [...prev, ...data.items]);
      }
    }
  }, [data, page]);

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [search, dateFrom, dateTo, paymentFilter]);

  const loadMore = useCallback(() => {
    if (data?.hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [data?.hasMore, isLoading]);

  // ─── Toggle deletion ──────────────────────────────────────────
  const toggleDeletionStatus = useCallback(
    (order: IOrderViewModel) => {
      const action = order.isDeleted ? "възстановите" : "изтриете";
      const stockWarning = order.isDeleted
        ? "Количествата на продуктите ще бъдат намалени отново."
        : "Количествата на продуктите ще бъдат възстановени и приходите ще бъдат изключени от репортите.";

      showCustomConfirmDialog(
        `Сигурни ли сте, че искате да ${action} поръчка ${order.displayNumber}? ${stockWarning}`,
        () => {
          mutate(
            {
              endpoint: `${endpointsMetadata.cashdesk.toggleDeletionStatus}?id=${order.id}`,
              method: "post",
              invalidateKeys: [
                [endpointsMetadata.cashdesk.getOrders],
                [endpointsMetadata.inventoryItemNomenclature.getAll],
                [endpointsMetadata.inventoryItemNomenclature.getAllCategorized],
              ],
            },
            {
              onSuccess: () => {
                setSelectedOrder(null);
                setPage(1);
              },
            },
          );
        },
      );
    },
    [mutate],
  );

  return {
    // Orders list
    orders: allOrders,
    totalCount: data?.totalCount ?? 0,
    hasMore: data?.hasMore ?? false,
    isLoading,
    loadMore,

    // Filters
    search,
    setSearch,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    paymentFilter,
    setPaymentFilter,

    // Detail
    selectedOrder,
    setSelectedOrder,
    toggleDeletionStatus,
  };
};
